import { ButtonType } from "@constellation-academy/epic-ui";
import { useFormik } from "formik";
import { forwardRef, useImperativeHandle } from "react";

import { toFormikValidationSchema } from "zod-formik-adapter";
import { useTranslation } from "@i18n/hooks";
import { cx } from "@styled-system/css";
import { OneTimePasswordRequestFormSchema } from "./one-time-password-request-form.consts";
import { inputsWrapperClass } from "./one-time-password-request-form.styles";
import {
	type OneTimePasswordRequestFormClass,
	type OneTimePasswordRequestFormProps,
	type OneTimePasswordRequestFormState,
} from "./one-time-password-request-form.types";
import { useAuthContext } from "../../../../../hooks/use-auth-context";
import { Button } from "../../../../button";
import { DefaultTextFieldComponent } from "../../../../DefaultTextInput";
import { lastUsedLoginMethodClass } from "../../../../social-login-button/social-login-button.styles";
import { ValidatedField } from "../../../../ValidatedField";

export const OneTimePasswordRequestForm = forwardRef<
	OneTimePasswordRequestFormClass,
	OneTimePasswordRequestFormProps
>(function OneTimePasswordRequestForm({ obSubmit, isSubmitting = false, buttonLabel }, ref) {
	const { t } = useTranslation("common");
	const { isLastLoginMethod } = useAuthContext();

	const form = useFormik<OneTimePasswordRequestFormState>({
		initialValues: { email: "" },
		validateOnChange: false,
		validateOnBlur: false,
		validationSchema: toFormikValidationSchema(OneTimePasswordRequestFormSchema),
		onSubmit: obSubmit,
	});

	useImperativeHandle(ref, () => ({
		values: form.values,
		submit: form.submitForm,
		reset: () => form.resetForm(),
	}));

	return (
		<form
			onSubmit={form.handleSubmit}
			className={cx(
				isLastLoginMethod("email") && lastUsedLoginMethodClass,
				inputsWrapperClass,
			)}
		>
			<ValidatedField<OneTimePasswordRequestFormState, string>
				formikConfig={form}
				name="email"
				placeholder={t("login.email")}
				component={DefaultTextFieldComponent}
			/>
			<Button
				type={ButtonType.Submit}
				label={buttonLabel ?? t("login.requestCodeDefault")}
				disabled={isSubmitting || !form.values.email}
			/>
		</form>
	);
});
export type OneTimePasswordRequestForm = OneTimePasswordRequestFormClass;
